<script lang="ts">
  import type { SkillCategory } from '$lib/api/types';
  import gsap from 'gsap';
  import Physics2DPlugin from 'gsap/Physics2DPlugin';
  import { onMount } from 'svelte';

  gsap.registerPlugin(Physics2DPlugin);

  interface Props {
    greyscaleOnIncomplete?: boolean;
    category: SkillCategory;
    completedSkillCount: number;
    possibleSkillCount: number;
    horizontal?: boolean;
    small?: boolean;
    animateOnMount?: boolean;
  }

  let {
    greyscaleOnIncomplete,
    category,
    completedSkillCount,
    possibleSkillCount,
    horizontal = false,
    small = false,
    animateOnMount = false,
  }: Props = $props();

  let image: HTMLImageElement;
  let imageAnimationComplete = $state(!animateOnMount);

  onMount(() => {
    if (animateOnMount) {
      confetti();
    }
  });

  export function launchAnimation() {
    confetti();
  }

  function confetti() {
    const cBadgeScaleDuration = 1.5;
    const cConfettiCountAvg = 50;
    const cConfettiCountRng = 10;

    const cConfettiDelay = 0.5;
    const cConfettiScaleDuration = 0.3;
    const cConfettiShootDuration = 3;
    const cConfettiScaleAvg = 0.7;
    const cConfettiScaleRng = 0.3;
    const cConfettiRotateAvg = 720;
    const cConfettiRotateRng = 360;
    const cConfettiVelocityAvg = 550;
    const cConfettiVelocityRng = 200;
    const cConfettiGravity = 800;

    // Generate a random number of dots
    const dotCount = gsap.utils.random(cConfettiCountAvg - cConfettiCountRng, cConfettiCountAvg + cConfettiCountAvg, 1);
    const colors = ['#FF8398', '#FF7288', '#ED6279', '#FFE7B4', '#F4D091']; // Define colors once

    const boundingRect = image.getBoundingClientRect();
    const startX = image.offsetLeft + boundingRect.width / 2 - 8;
    const startY = image.offsetTop + boundingRect.height / 2 - 8;

    gsap.set(image, {
      scale: 0,
      rotateY: 180,
      filter: 'grayscale(100%)',
      opacity: 1,
    });

    gsap
      .timeline({
        onComplete: () => {
          // layer badge underneath confetti. idea is confetti comes out from behind badge but falls in front of badge
          gsap.set(image, {
            zIndex: 8,
            rotateX: 0,
          });
          imageAnimationComplete = true;
        },
      })
      .to(image, {
        duration: cBadgeScaleDuration,
        scale: 1,
        rotateY: 0,
        filter: 'grayscale(0%)',
        ease: 'power3.out',
      });

    const confettiScaleMin = cConfettiScaleAvg - cConfettiScaleRng;
    const confettiScaleMax = cConfettiScaleAvg + cConfettiScaleRng;
    const confettiRotateMin = cConfettiRotateAvg - cConfettiRotateRng;
    const confettiRotateMax = cConfettiRotateAvg + cConfettiRotateRng;
    const confettiVelocityMin = cConfettiVelocityAvg - cConfettiVelocityRng;
    const confettiVelocityMax = cConfettiVelocityAvg + cConfettiVelocityRng;

    for (let i = 0; i < dotCount; i++) {
      // Create a dot element
      const dot = document.createElement('div');
      dot.classList.add('dot');

      // Append the dot to the body
      document.body.appendChild(dot);

      // Set initial position and styles of the dot
      gsap.set(dot, {
        backgroundColor: gsap.utils.random(colors), // Pick a random color
        left: startX,
        top: startY, // position dot at coordinates of the click
        scale: 0, // Start at scale 0
      });

      // Animate the dot with physics2D
      gsap
        .timeline({
          delay: cConfettiDelay,
          onComplete: () => dot.remove(), // Remove the dot after animation
        })
        .to(dot, {
          scale: gsap.utils.random(confettiScaleMin, confettiScaleMax), // Scale between 0.5 and 1
          duration: cConfettiScaleDuration, // Quick pop-in effect
          // rotateY: gsap.utils.random(0, 360),
          ease: 'power3.out',
        })
        .to(
          dot,
          {
            duration: cConfettiShootDuration,
            rotateY: gsap.utils.random(confettiRotateMin, confettiRotateMax),
            rotateX: gsap.utils.random(confettiRotateMin, confettiRotateMax),
            rotateZ: gsap.utils.random(confettiRotateMin, confettiRotateMax),
            physics2D: {
              velocity: gsap.utils.random(confettiVelocityMin, confettiVelocityMax), // Random velocity
              angle: gsap.utils.random(0, 360), // Random direction
              gravity: cConfettiGravity, // Gravity effect
            },
            autoAlpha: 0, // Fade out towards the end
            ease: 'none',
          },
          '<',
        ); // Start together with the previous tween
    }
  }
</script>

<div class="grid-item" class:horizontal>
  <img
    bind:this={image}
    alt="badge for {category.name}"
    src={category.badge}
    class="category-badge"
    class:incomplete={greyscaleOnIncomplete && completedSkillCount < possibleSkillCount}
    class:animation-complete={imageAnimationComplete}
    class:small
  />
  <div>
    <h4 class="mb-0">{category.name}</h4>
    <div aria-label="{completedSkillCount} completed skills out of {possibleSkillCount} possible">
      {completedSkillCount}/{possibleSkillCount}
    </div>
  </div>
</div>

<style>
  .category-badge {
    width: 128px;
    height: 128px;
    display: block;
    z-index: 10;
    backface-visibility: hidden;
    opacity: 0;
  }

  .category-badge.animation-complete {
    opacity: 1;
  }

  .grid-item {
    min-width: 150px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    text-align: center;
  }

  .grid-item.horizontal {
    flex-direction: row;
    text-align: left;
  }

  .category-badge.incomplete {
    filter: grayscale(100%);
  }

  .category-badge.small {
    width: 64px;
    height: 64px;
  }
</style>
